import React, { createContext, useContext, ReactNode } from 'react'
import {
  useClaimsToggle,
  useUserCognitoGroups,
} from '@npm-client-frontends/shared-hooks'
import {
  AudienceGroups,
  DatePreference,
} from '@npm-client-frontends/client-frontends-shared-types'
import { RegionMode } from '@npm-client-frontends/shared-constants'
import { useUserAttributes } from '@npm-client-frontends/global-context'

interface UserPreferencesContextType {
  userPreferencesHaveLoaded: boolean
  userDateFormat: DatePreference
  globalClaims: AudienceGroups[]
  globalRegionMode: RegionMode | null
  selectedClaims: AudienceGroups[]
  selectedRegionMode: RegionMode | null
}

const UserPreferencesContext = createContext<
  UserPreferencesContextType | undefined
>(undefined)

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext)
  if (context === undefined) {
    throw new Error(
      'useUserPreferences must be used within a UserPreferencesProvider',
    )
  }
  return context
}

export const UserPreferencesProvider = ({
  children,
  userDateFormat,
  globalClaims,
  globalRegionMode,
  selectedClaims,
  selectedRegionMode,
  userPreferencesHaveLoaded,
}: {
  children: ReactNode
} & UserPreferencesContextType) => {
  return (
    <UserPreferencesContext.Provider
      value={{
        userDateFormat,
        globalClaims,
        globalRegionMode,
        selectedClaims,
        selectedRegionMode,
        userPreferencesHaveLoaded,
      }}
    >
      {children}
    </UserPreferencesContext.Provider>
  )
}

export const UserPreferencesProviderWrapper = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { userDateFormat } = useUserAttributes()
  const { selectedClaims, selectedRegionMode, globalRegionMode } =
    useClaimsToggle()
  const { userAudienceGroups } = useUserCognitoGroups()

  return (
    <UserPreferencesProvider
      userDateFormat={userDateFormat}
      globalClaims={userAudienceGroups}
      globalRegionMode={globalRegionMode}
      selectedClaims={selectedClaims}
      selectedRegionMode={selectedRegionMode}
      userPreferencesHaveLoaded={!!userDateFormat && !!selectedRegionMode}
    >
      {children}
    </UserPreferencesProvider>
  )
  return <>{children}</>
}
